import { createSlice, isAnyOf } from "@reduxjs/toolkit";
import {
  reservationLoadingGroups,
  reservationThunks,
  fetchAteliers,
  createAtelier,
  updateAtelier,
  deleteAtelier,
  fetchZones,
  createZone,
  updateZone,
  deleteZone,
  fetchDisponibiliteMois,
  fetchDisponibiliteJour,
  fetchMonthReservations,
  sendReservation,
  fetchReservationsBySlot,
} from "./thunks";
import {
  buildLoadingActionMap,
  createLoadingMatcher,
  createLoadingSliceState,
} from "./loadingTracker";

const rejectionMatcher = isAnyOf(
  ...reservationThunks.map((thunk) => thunk.rejected)
);

const pendingMatcher = isAnyOf(
  ...reservationThunks.map((thunk) => thunk.pending)
);

const loadingActionMap = buildLoadingActionMap(reservationLoadingGroups);

const reservationSlice = createSlice({
  name: "reservation",
  initialState: {
    ateliers: [],
    zones: [],
    creneaux: [],
    creneauxJour: [],
    monthReservations: [],
    reservationDetails: [],
    loading: false,
    loadingByKey: createLoadingSliceState(Object.keys(reservationLoadingGroups)),
    error: null,
    reservationStatus: "idle", // "idle" | "loading" | "success" | "error"
    reservationResponse: null, // pour stocker la réponse du backend
  },
  reducers: {
    resetReservationState: (state) => {
      state.reservationStatus = "idle";
      state.error = null;
      state.reservationResponse = null;
      Object.keys(state.loadingByKey).forEach((key) => {
        state.loadingByKey[key] = false;
      });
      state.loading = false;
      state.reservationDetails = [];
    },
    clearReservationDetails: (state) => {
      state.reservationDetails = [];
    },
    clearDisponibiliteMois: (state) => {
      state.creneaux = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // -------------------------- Section ATELIERS -------------------------
      .addCase(fetchAteliers.fulfilled, (state, action) => {
        state.ateliers = action.payload;
      })
      .addCase(createAtelier.fulfilled, (state, action) => {
        state.ateliers.push(action.payload);
      })
      .addCase(updateAtelier.fulfilled, (state, action) => {
        const index = state.ateliers.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) state.ateliers[index] = action.payload;
      })
      .addCase(deleteAtelier.fulfilled, (state, action) => {
        state.ateliers = state.ateliers.filter((a) => a.id !== action.payload);
      })
      // -------------------------- Section ZONES -------------------------
      .addCase(fetchZones.fulfilled, (state, action) => {
        state.zones = action.payload;
      })
      .addCase(createZone.fulfilled, (state, action) => {
        state.zones.push(action.payload);
      })
      .addCase(updateZone.fulfilled, (state, action) => {
        const index = state.zones.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) state.zones[index] = action.payload;
      })
      .addCase(deleteZone.fulfilled, (state, action) => {
        state.zones = state.zones.filter((a) => a.id !== action.payload);
      })
      // -------------------------- Section DISPONIBILITÉ -------------------------
      .addCase(fetchDisponibiliteMois.fulfilled, (state, action) => {
        state.creneaux = action.payload;
      })
      .addCase(fetchDisponibiliteJour.fulfilled, (state, action) => {
        state.creneauxJour = action.payload;
      })
      .addCase(fetchMonthReservations.fulfilled, (state, action) => {
        state.monthReservations = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      // -------------------------- Section RÉSERVATION -------------------------
      .addCase(sendReservation.pending, (state) => {
        state.reservationStatus = "loading";
        state.error = null;
      })
      .addCase(sendReservation.fulfilled, (state, action) => {
        state.reservationStatus = "success";
        state.reservationResponse = action.payload;
        state.error = null;
      })
      .addCase(sendReservation.rejected, (state, action) => {
        state.reservationStatus = "error";
        state.error = action.payload || action.error?.message || null;
      })
      .addCase(fetchReservationsBySlot.fulfilled, (state, action) => {
        state.reservationDetails = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addMatcher(createLoadingMatcher(loadingActionMap), (state, action) => {
        const target = loadingActionMap[action.type];
        if (!target) return;

        const isPending = action.type.endsWith("/pending");
        state.loadingByKey[target] = isPending;
        state.loading = Object.values(state.loadingByKey).some(Boolean);

        if (isPending) {
          state.error = null;
        }
      })
      .addMatcher(pendingMatcher, (state) => {
        state.error = null;
      })
      .addMatcher(rejectionMatcher, (state, action) => {
        state.error =
          action.payload || action.error?.message || "Une erreur est survenue";
      });
  },
});

export default reservationSlice;
export const {
  resetReservationState,
  clearReservationDetails,
  clearDisponibiliteMois,
} = reservationSlice.actions;
